import { TIMEFRAME_LABELS } from '@shared/types/candle'
import type { IChartApi, MouseEventParams } from 'lightweight-charts'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  Code,
  Eye,
  EyeOff,
  Minus,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CustomIndicatorPanel } from '../customIndicator/CustomIndicatorPanel'
import { IndicatorManager } from '../indicators/IndicatorManager'

import { ChartCanvas, RIGHT_OFFSET } from './ChartCanvas'
import type { AnySeriesApi } from './ChartCanvas'
import { ChartToolbar } from './ChartToolbar'
import { DrawingCanvas } from './DrawingCanvas'
import { IndicatorPane, PANE_H } from './IndicatorPane'

import type { OhlcvBar } from '@/hooks/useCandles'
import { useCandles } from '@/hooks/useCandles'
import { useChartTheme } from '@/hooks/useChartTheme'
import { useCustomIndicatorValues } from '@/hooks/useCustomIndicatorValues'
import { useIndicatorValues } from '@/hooks/useIndicatorValues'
import { useLiveCandle } from '@/hooks/useLiveCandle'
import { useLiveTick } from '@/hooks/useLiveTick'
import { getIndicator } from '@/indicators/registry'
import { useChartStore } from '@/stores/chartStore'
import { useCustomIndicatorStore } from '@/stores/customIndicatorStore'
import { useDrawingStore } from '@/stores/drawingStore'
import { useLayoutStore } from '@/stores/layoutStore'
import { usePineStore } from '@/stores/pineStore'
import { useTemplateStore } from '@/stores/templateStore'
import { cn } from '@/utils/cn'
import { formatPrice, formatVolume } from '@/utils/format'

const MIN_PANE_H = 48
const MIN_MAIN_H = 80

interface ChartContainerProps {
  panelId: string
}

function ResizeDivider({ onDragStart }: { onDragStart: (startY: number) => void }) {
  return (
    <div
      className="group relative z-10 h-1 cursor-ns-resize select-none bg-tv-border transition-colors hover:bg-tv-blue/60"
      onMouseDown={(e) => {
        e.preventDefault()
        onDragStart(e.clientY)
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center gap-1 opacity-0 group-hover:opacity-60">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-0.5 w-3 rounded-full bg-tv-text-muted" />
        ))}
      </div>
    </div>
  )
}

export function ChartContainer({ panelId }: ChartContainerProps) {
  const {
    panels,
    activePanelId,
    setActivePanel,
    addIndicator,
    removeIndicator,
    updateIndicator,
    toggleIndicatorEnabled,
    reorderIndicators,
    attachCustomIndicator,
    detachCustomIndicator,
    toggleCustomIndicatorEnabled,
    reorderCustomIndicators,
    applyTemplate,
  } = useChartStore()
  const { templates, saveTemplate, deleteTemplate } = useTemplateStore()
  const { activeTool } = useLayoutStore()
  const { drawings, addDrawing, removeDrawing, updateDrawing } = useDrawingStore()
  const { indicators: customDefinitions } = useCustomIndicatorStore()
  const { scripts: pineScripts } = usePineStore()

  const panelDrawings = drawings[panelId] ?? []
  const panel = panels[panelId]
  const isActive = activePanelId === panelId
  const { isDark } = useChartTheme()
  const [isManagerOpen, setIsManagerOpen] = useState(false)
  const [isCustomIndicatorOpen, setIsCustomIndicatorOpen] = useState(false)
  const [atLatest, setAtLatest] = useState(true)
  const [crosshairBar, setCrosshairBar] = useState<OhlcvBar | null>(null)

  // ── Vertical pane resize ──────────────────────────────────────────────────
  const [paneHeights, setPaneHeights] = useState<Record<string, number>>({})
  const wrapperRef = useRef<HTMLDivElement>(null)

  const startResize = (instanceId: string, indicatorId: string, startY: number) => {
    const startH = paneHeights[instanceId] ?? PANE_H(indicatorId)
    const wrapperH = wrapperRef.current?.clientHeight ?? Infinity
    const otherSubH = (panel?.indicators ?? [])
      .filter((ind) => {
        const entry = getIndicator(ind.id)
        return entry && !entry.config.overlay && ind.instanceId !== instanceId
      })
      .reduce((sum, ind) => sum + (paneHeights[ind.instanceId] ?? PANE_H(ind.id)), 0)
    const maxH = wrapperH - otherSubH - MIN_MAIN_H

    const onMove = (me: MouseEvent) => {
      const newH = Math.min(maxH, Math.max(MIN_PANE_H, startH - (me.clientY - startY)))
      setPaneHeights((prev) => ({ ...prev, [instanceId]: newH }))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const {
    data: bars = [],
    isLoading,
    isError,
  } = useCandles(panel?.symbol ?? '', panel?.timeframe ?? '1D')

  const liveTick = useLiveTick(panel?.symbol ?? '')
  const liveCandle = useLiveCandle(panel?.symbol ?? '', panel?.timeframe ?? '1D')

  const indicators = useMemo(() => panel?.indicators ?? [], [panel?.indicators])
  const customInstances = useMemo(() => panel?.customInstances ?? [], [panel?.customInstances])

  // Only pass enabled built-in indicators to the value calculator
  const enabledIndicators = useMemo(
    () => indicators.filter((ind) => ind.enabled ?? true),
    [indicators]
  )

  // Merge liveCandle into bars so indicators (VWAP, etc.) update in real-time.
  // If liveCandle has the same timestamp as the last bar → replace it (in-progress update).
  // If liveCandle has a newer timestamp → append it (new bar started).
  const barsWithLive = useMemo(() => {
    if (!liveCandle || bars.length === 0) return bars
    const last = bars[bars.length - 1]
    if (liveCandle.time === last.time) return [...bars.slice(0, -1), liveCandle]
    if (liveCandle.time > last.time) return [...bars, liveCandle]
    return bars
  }, [bars, liveCandle])

  const indicatorValues = useIndicatorValues(barsWithLive, enabledIndicators)

  // Build definition lookup map: custom JS + compiled Pine scripts
  const definitionsMap = useMemo(() => {
    const m = new Map(customDefinitions.map((d) => [d.id, d]))
    for (const sc of pineScripts) {
      m.set(sc.id, {
        id: sc.id,
        name: sc.title,
        description: sc.description,
        code: sc.compiledCode,
        color: '#2962ff',
        enabled: sc.enabled,
        createdAt: sc.createdAt,
        updatedAt: sc.updatedAt,
      })
    }
    return m
  }, [customDefinitions, pineScripts])
  const { results: customIndicatorResults, errors: customIndicatorErrors } =
    useCustomIndicatorValues(customInstances, definitionsMap, barsWithLive)

  const barCountRef = useRef(bars.length)
  const barsRef = useRef(bars)
  useEffect(() => {
    barCountRef.current = bars.length
    barsRef.current = bars
  }, [bars])

  // ── Chart sync ────────────────────────────────────────────────────────────
  const mainChartRef = useRef<IChartApi | null>(null)
  const mainSeriesRef = useRef<AnySeriesApi | null>(null)
  const subChartsRef = useRef<IChartApi[]>([])
  const syncingRef = useRef(false)
  // Updated every render so buildCompositeCanvas always draws fresh overlay data
  const drawOverlayRef = useRef<(ctx: CanvasRenderingContext2D, dpr: number) => void>(() => {})

  const registerMainChart = useCallback((chart: IChartApi) => {
    mainChartRef.current = chart
    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range) return
      setAtLatest(range.to >= barCountRef.current - 2)
      if (syncingRef.current) return
      syncingRef.current = true
      subChartsRef.current.forEach((c) => c.timeScale().setVisibleLogicalRange(range))
      syncingRef.current = false
    })
    chart.subscribeCrosshairMove((params: MouseEventParams) => {
      if (!params.time) {
        setCrosshairBar(null)
        return
      }
      const t = params.time as number
      const bar = barsRef.current.find((b) => b.time === t) ?? null
      setCrosshairBar(bar)
    })
  }, [])

  const handleMainSeriesReady = useCallback((series: AnySeriesApi) => {
    mainSeriesRef.current = series
  }, [])

  const registerSubChart = useCallback((chart: IChartApi) => {
    subChartsRef.current.push(chart)
  }, [])

  const syncSubChartNow = useCallback((chart: IChartApi) => {
    const range = mainChartRef.current?.timeScale().getVisibleLogicalRange()
    if (range) chart.timeScale().setVisibleLogicalRange(range)
  }, [])

  const unregisterChart = useCallback((chart: IChartApi) => {
    subChartsRef.current = subChartsRef.current.filter((c) => c !== chart)
  }, [])

  // ── Templates ─────────────────────────────────────────────────────────────
  const handleSaveTemplate = useCallback(
    (name: string) => {
      if (!panel) return
      saveTemplate(name, {
        chartType: panel.chartType,
        timeframe: panel.timeframe,
        indicators: panel.indicators.map(({ instanceId: _id, ...rest }) => rest),
      })
    },
    [panel, saveTemplate]
  )

  const handleApplyTemplate = useCallback(
    (templateId: string) => {
      const template = templates.find((t) => t.id === templateId)
      if (template) applyTemplate(panelId, template)
    },
    [templates, applyTemplate, panelId]
  )

  // ── Screenshot ────────────────────────────────────────────────────────────
  function buildCompositeCanvas(): HTMLCanvasElement | null {
    const mainChart = mainChartRef.current
    if (!mainChart) return null
    const canvases = [mainChart, ...subChartsRef.current].map((c) => c.takeScreenshot())
    if (canvases.length === 0) return null

    const width = canvases[0].width
    const totalHeight = canvases.reduce((sum, c) => sum + c.height, 0)
    const composite = document.createElement('canvas')
    composite.width = width
    composite.height = totalHeight
    const ctx = composite.getContext('2d')
    if (!ctx) return canvases[0]
    let y = 0
    for (const c of canvases) {
      ctx.drawImage(c, 0, y)
      y += c.height
    }
    // Draw the same overlay that appears in the DOM
    const dpr = window.devicePixelRatio || 1
    drawOverlayRef.current(ctx, dpr)
    return composite
  }

  const handleDownloadImage = useCallback(() => {
    const canvas = buildCompositeCanvas()
    if (!canvas) return
    canvas.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${panel?.symbol ?? 'chart'}_${panel?.timeframe ?? ''}_${Date.now()}.png`
      a.click()
      URL.revokeObjectURL(url)
    })
  }, [panel])

  const handleCopyImage = useCallback(() => {
    const canvas = buildCompositeCanvas()
    if (!canvas) return
    canvas.toBlob((blob) => {
      if (!blob) return
      void navigator.clipboard
        .write([new ClipboardItem({ 'image/png': blob })])
        .catch(() => undefined)
    })
  }, [])

  // ── Navigation ────────────────────────────────────────────────────────────
  function zoomBy(factor: number) {
    const ts = mainChartRef.current?.timeScale()
    if (!ts) return
    const range = ts.getVisibleLogicalRange()
    if (!range) return
    const mid = (range.from + range.to) / 2
    const half = ((range.to - range.from) / 2) * factor
    ts.setVisibleLogicalRange({ from: mid - half, to: mid + half })
  }

  function scrollBy(direction: 1 | -1) {
    const ts = mainChartRef.current?.timeScale()
    if (!ts) return
    const range = ts.getVisibleLogicalRange()
    if (!range) return
    const visibleBars = range.to - range.from
    const step = Math.max(3, Math.round(visibleBars * 0.2))
    const currentRightOff = range.to - (barCountRef.current - 1)
    ts.scrollToPosition(currentRightOff - direction * step, false)
  }

  function handleReset() {
    const ts = mainChartRef.current?.timeScale()
    if (!ts) return
    ts.fitContent()
    ts.applyOptions({ rightOffset: RIGHT_OFFSET })
  }

  function handleGoToLatest() {
    mainChartRef.current?.timeScale().scrollToRealTime()
  }

  if (!panel) return null

  // Sub-pane indicators: enabled built-in non-overlay
  const subPaneIndicators = enabledIndicators.filter((ind) => {
    const entry = getIndicator(ind.id)
    return entry && !entry.config.overlay
  })

  const totalIndicatorCount =
    indicators.filter((i) => i.enabled ?? true).length +
    customInstances.filter((i) => i.enabled).length

  // ── Legend items for overlay indicators ──────────────────────────────────
  const overlayLegendItems = indicators
    .filter((ind) => {
      const entry = getIndicator(ind.id)
      return entry?.config.overlay
    })
    .map((ind) => {
      const entry = getIndicator(ind.id)!
      const cfg = entry.config
      const paramStr = cfg.params.map((p) => ind.params[p.key] ?? p.default).join(', ')
      const result = indicatorValues.get(ind.instanceId)
      const firstLine = result?.lines[0]
      const lastVal = firstLine ? [...firstLine.values].reverse().find((v) => v != null) : null
      const color = ind.colors?.[cfg.lines[0]?.key] ?? cfg.lines[0]?.color ?? '#2962ff'
      return {
        key: ind.instanceId,
        color,
        name: `${cfg.shortName} (${paramStr})`,
        value: lastVal != null ? lastVal.toFixed(2) : '—',
        enabled: ind.enabled ?? true,
        type: 'builtin' as const,
        instanceId: ind.instanceId,
      }
    })

  const customLegendItems = customInstances.map((inst) => {
    const def = definitionsMap.get(inst.definitionId)
    const plots = customIndicatorResults.get(inst.instanceId) ?? []
    const firstPlot = plots[0]
    const lastVal = firstPlot ? [...firstPlot.values].reverse().find((v) => v != null) : null
    return {
      key: inst.instanceId,
      color: firstPlot?.color ?? def?.color ?? '#2962ff',
      name: def?.name ?? 'Custom',
      value: lastVal != null ? lastVal.toFixed(2) : '—',
      enabled: inst.enabled,
      type: 'custom' as const,
      instanceId: inst.instanceId,
      definitionId: inst.definitionId,
    }
  })

  const legendItems = [...overlayLegendItems, ...customLegendItems]

  // ── Screenshot overlay drawing (updated every render so it stays fresh) ──
  const _displayBarForShot =
    crosshairBar ?? liveCandle ?? (bars.length > 0 ? bars[bars.length - 1] : null)
  const _displayIdxForShot = crosshairBar
    ? bars.findIndex((b) => b.time === crosshairBar.time)
    : bars.length - 1
  const _prevBarForShot = _displayIdxForShot > 0 ? bars[_displayIdxForShot - 1] : null
  const _tfLabelForShot = TIMEFRAME_LABELS[panel.timeframe] ?? panel.timeframe
  const _isDarkForShot = isDark
  const _symbolForShot = panel.symbol
  const _legendItemsForShot = legendItems
  drawOverlayRef.current = (ctx: CanvasRenderingContext2D, dpr: number) => {
    const C = _isDarkForShot
      ? { text: '#d1d4dc', dim: '#787b86', muted: '#4c525e', green: '#26a69a', red: '#ef5350' }
      : { text: '#131722', dim: '#4c525e', muted: '#787b86', green: '#089981', red: '#e53935' }

    const px = (n: number) => Math.round(n * dpr)
    const fontSize = px(11)
    const lineH = px(16)
    const monoFont = `${fontSize}px 'JetBrains Mono', Menlo, monospace`
    const boldFont = `bold ${px(12)}px Inter, system-ui, sans-serif`
    const sansFont = `${fontSize}px Inter, system-ui, sans-serif`

    ctx.textBaseline = 'alphabetic'
    const x = px(6)
    let y = px(18)

    if (_displayBarForShot) {
      const b = _displayBarForShot
      const prevClose = _prevBarForShot?.close ?? b.open
      const chg = b.close - prevClose
      const chgPct = prevClose !== 0 ? (chg / prevClose) * 100 : 0
      const isUp = chg >= 0

      // Symbol + timeframe
      ctx.font = boldFont
      ctx.fillStyle = C.text
      ctx.fillText(_symbolForShot, x, y)
      const symW = ctx.measureText(_symbolForShot).width
      ctx.font = sansFont
      ctx.fillStyle = C.muted
      ctx.fillText(` · ${_tfLabelForShot}`, x + symW, y)
      y += lineH

      // OHLC + change
      ctx.font = monoFont
      let xp = x
      const pairs: [string, string, string][] = [
        ['O', formatPrice(b.open), C.text],
        ['H', formatPrice(b.high), C.green],
        ['L', formatPrice(b.low), C.red],
        ['C', formatPrice(b.close), C.text],
      ]
      for (const [label, val, valColor] of pairs) {
        ctx.fillStyle = C.dim
        ctx.fillText(label, xp, y)
        xp += ctx.measureText(label).width + px(2)
        ctx.fillStyle = valColor
        ctx.fillText(val, xp, y)
        xp += ctx.measureText(val).width + px(10)
      }
      // Change
      const chgStr = `${isUp ? '+' : ''}${formatPrice(chg)} (${isUp ? '+' : ''}${chgPct.toFixed(2)}%)`
      ctx.fillStyle = isUp ? C.green : C.red
      ctx.fillText(chgStr, xp, y)
      y += lineH

      // Volume
      if (b.value != null && b.value > 0) {
        ctx.fillStyle = C.dim
        ctx.fillText('Vol ', x, y)
        ctx.fillStyle = C.text
        ctx.fillText(formatVolume(b.value), x + ctx.measureText('Vol ').width, y)
        y += lineH
      }
    }

    // Indicator legend
    for (const item of _legendItemsForShot) {
      const dotR = px(3.5)
      const dotX = x + dotR
      const dotY = y - px(3.5)
      ctx.beginPath()
      ctx.arc(dotX, dotY, dotR, 0, Math.PI * 2)
      ctx.fillStyle = item.color
      ctx.fill()

      ctx.font = monoFont
      ctx.fillStyle = item.enabled ? C.text : C.muted
      const nameX = x + px(11)
      ctx.fillText(item.name, nameX, y)
      if (item.enabled) {
        ctx.fillStyle = C.dim
        ctx.fillText(`  ${item.value}`, nameX + ctx.measureText(item.name).width, y)
      }
      y += lineH
    }
  }

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col overflow-hidden',
        isActive ? 'ring-1 ring-inset ring-tv-blue/40' : ''
      )}
      onClick={() => setActivePanel(panelId)}
    >
      <ChartToolbar
        panelId={panelId}
        isActive={isActive}
        onActivate={() => setActivePanel(panelId)}
        liveTick={liveTick}
        onIndicatorClick={() => setIsManagerOpen(true)}
        activeIndicatorCount={totalIndicatorCount}
        templates={templates}
        onSaveTemplate={handleSaveTemplate}
        onApplyTemplate={handleApplyTemplate}
        onDeleteTemplate={deleteTemplate}
        onDownloadImage={handleDownloadImage}
        onCopyImage={handleCopyImage}
        onCustomIndicatorClick={() => setIsCustomIndicatorOpen(true)}
        customIndicatorCount={customDefinitions.length + pineScripts.length}
      />

      <div ref={wrapperRef} className="relative flex flex-1 flex-col overflow-hidden">
        <div className="relative flex-1 overflow-hidden">
          <ChartCanvas
            bars={bars}
            chartType={panel.chartType}
            timeframe={panel.timeframe}
            indicators={enabledIndicators}
            indicatorValues={indicatorValues}
            customIndicatorResults={customIndicatorResults}
            isLoading={isLoading}
            isError={isError}
            liveTick={liveTick}
            liveCandle={liveCandle}
            onChartReady={registerMainChart}
            onChartDestroy={unregisterChart}
            onMainSeriesReady={handleMainSeriesReady}
          />
          <DrawingCanvas
            chartRef={mainChartRef}
            seriesRef={mainSeriesRef}
            activeTool={activeTool}
            panelId={panelId}
            drawings={panelDrawings}
            onAdd={(d) => addDrawing(panelId, d)}
            onRemove={(id) => removeDrawing(panelId, id)}
            onUpdate={(id, patch) => updateDrawing(panelId, id, patch)}
          />

          {/* OHLCV info + indicator legend overlay */}
          <div className="absolute left-1 top-1 z-10 flex flex-col gap-0.5">
            {/* OHLCV header row — symbol+TF always visible; prices only when a bar exists */}
            {(() => {
              const displayBar =
                crosshairBar ?? liveCandle ?? (bars.length > 0 ? bars[bars.length - 1] : null)
              const displayIdx = crosshairBar
                ? bars.findIndex((b) => b.time === crosshairBar.time)
                : bars.length - 1
              const prevBar = displayIdx > 0 ? bars[displayIdx - 1] : null
              const prevClose = prevBar?.close ?? displayBar?.open ?? 0
              const chg = displayBar ? displayBar.close - prevClose : 0
              const chgPct = prevClose !== 0 ? (chg / prevClose) * 100 : 0
              const isUp = chg >= 0
              const tfLabel = TIMEFRAME_LABELS[panel.timeframe] ?? panel.timeframe
              return (
                <div className="flex flex-col gap-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0 px-1 py-0.5">
                    <span className="text-[12px] font-semibold text-tv-text-primary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                      {panel.symbol}
                    </span>
                    <span className="text-[11px] text-tv-text-muted drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                      {tfLabel}
                    </span>
                    {displayBar ? (
                      <>
                        <span className="font-mono text-[11px] text-tv-text-secondary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                          O
                          <span className="ml-0.5 text-tv-text-primary">
                            {formatPrice(displayBar.open)}
                          </span>
                        </span>
                        <span className="font-mono text-[11px] text-tv-text-secondary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                          H
                          <span className="ml-0.5 text-tv-green">
                            {formatPrice(displayBar.high)}
                          </span>
                        </span>
                        <span className="font-mono text-[11px] text-tv-text-secondary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                          L<span className="ml-0.5 text-tv-red">{formatPrice(displayBar.low)}</span>
                        </span>
                        <span className="font-mono text-[11px] text-tv-text-secondary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                          C
                          <span className="ml-0.5 text-tv-text-primary">
                            {formatPrice(displayBar.close)}
                          </span>
                        </span>
                        <span
                          className={cn(
                            'font-mono text-[11px] drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]',
                            isUp ? 'text-tv-green' : 'text-tv-red'
                          )}
                        >
                          {isUp ? '+' : ''}
                          {formatPrice(chg)} ({isUp ? '+' : ''}
                          {chgPct.toFixed(2)}%)
                        </span>
                      </>
                    ) : !isLoading ? (
                      <span className="text-[11px] text-tv-text-muted drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                        No data
                      </span>
                    ) : null}
                  </div>
                  {displayBar?.value != null && displayBar.value > 0 && (
                    <div className="px-1 py-0">
                      <span className="font-mono text-[11px] text-tv-text-secondary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                        Vol{' '}
                        <span className="text-tv-text-primary">
                          {formatVolume(displayBar.value)}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Indicator legend rows */}
            {legendItems.map((item) => (
              <div
                key={item.key}
                className={cn(
                  'group pointer-events-auto flex items-center gap-1 rounded px-1 py-0.5',
                  !item.enabled && 'opacity-40'
                )}
              >
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="font-mono text-[11px] text-tv-text-primary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                  {item.name}
                </span>
                {item.enabled && (
                  <span className="font-mono text-[11px] text-tv-text-secondary drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]">
                    {item.value}
                  </span>
                )}
                {/* Action buttons — visible on hover */}
                <div className="ml-0.5 hidden items-center gap-0.5 group-hover:flex">
                  <button
                    type="button"
                    title={item.enabled ? 'Hide' : 'Show'}
                    onClick={(e) => {
                      e.stopPropagation()
                      item.type === 'builtin'
                        ? toggleIndicatorEnabled(panelId, item.instanceId)
                        : toggleCustomIndicatorEnabled(panelId, item.instanceId)
                    }}
                    className="rounded p-0.5 text-tv-text-muted hover:bg-tv-bg-secondary hover:text-tv-text-primary"
                  >
                    {item.enabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  </button>
                  {item.type === 'custom' && (
                    <button
                      type="button"
                      title="Edit source"
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsCustomIndicatorOpen(true)
                      }}
                      className="rounded p-0.5 text-tv-text-muted hover:bg-tv-bg-secondary hover:text-tv-text-primary"
                    >
                      <Code className="h-3 w-3" />
                    </button>
                  )}
                  <button
                    type="button"
                    title="Remove"
                    onClick={(e) => {
                      e.stopPropagation()
                      item.type === 'builtin'
                        ? removeIndicator(panelId, item.instanceId)
                        : detachCustomIndicator(panelId, item.instanceId)
                    }}
                    className="rounded p-0.5 text-tv-text-muted hover:bg-tv-bg-secondary hover:text-tv-red"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {subPaneIndicators.map((ind) => {
          const result = indicatorValues.get(ind.instanceId)
          if (!result) return null
          return (
            <div key={ind.instanceId} className="shrink-0">
              <ResizeDivider
                onDragStart={(startY) => startResize(ind.instanceId, ind.id, startY)}
              />
              <IndicatorPane
                bars={bars}
                indicator={ind}
                result={result}
                isDark={isDark}
                height={paneHeights[ind.instanceId]}
                onRemove={() => removeIndicator(panelId, ind.instanceId)}
                onChartReady={registerSubChart}
                onDataReady={syncSubChartNow}
                onChartDestroy={unregisterChart}
              />
            </div>
          )
        })}

        {/* Navigation */}
        <div className="pointer-events-none absolute bottom-2 left-1/2 z-20 -translate-x-1/2">
          <div className="pointer-events-auto flex items-center overflow-hidden rounded border border-tv-border bg-tv-bg-secondary shadow-sm">
            <NavBtn onClick={() => zoomBy(1.4)} title="Zoom out">
              <Minus className="h-3 w-3" />
            </NavBtn>
            <div className="h-3.5 w-px bg-tv-border" />
            <NavBtn onClick={() => zoomBy(0.65)} title="Zoom in">
              <Plus className="h-3 w-3" />
            </NavBtn>
            <div className="h-3.5 w-px bg-tv-border" />
            <NavBtn onClick={() => scrollBy(-1)} title="Scroll left">
              <ChevronLeft className="h-3.5 w-3.5" />
            </NavBtn>
            <div className="h-3.5 w-px bg-tv-border" />
            <NavBtn onClick={() => scrollBy(1)} title="Scroll right">
              <ChevronRight className="h-3.5 w-3.5" />
            </NavBtn>
            <div className="h-3.5 w-px bg-tv-border" />
            <NavBtn onClick={handleReset} title="Reset chart view">
              <RotateCcw className="h-3 w-3" />
            </NavBtn>
          </div>
        </div>

        {!atLatest && bars.length > 0 && (
          <button
            type="button"
            onClick={handleGoToLatest}
            title="Go to latest"
            className="pointer-events-auto absolute bottom-2 right-14 z-20 flex h-6 w-6 items-center justify-center rounded border border-tv-border bg-tv-bg-secondary shadow-sm hover:bg-tv-bg-tertiary hover:text-tv-blue"
          >
            <ChevronsRight className="h-3.5 w-3.5 text-tv-text-secondary" />
          </button>
        )}
      </div>

      {/* Unified Indicator Manager */}
      {isManagerOpen && (
        <IndicatorManager
          panelId={panelId}
          builtinIndicators={indicators}
          customInstances={customInstances}
          onAddBuiltin={(ind) => addIndicator(panelId, ind)}
          onRemoveBuiltin={(instanceId) => removeIndicator(panelId, instanceId)}
          onUpdateBuiltin={(instanceId, updates) => updateIndicator(panelId, instanceId, updates)}
          onToggleBuiltin={(instanceId) => toggleIndicatorEnabled(panelId, instanceId)}
          onReorderBuiltin={(from, to) => reorderIndicators(panelId, from, to)}
          onAttachCustom={(defId) => attachCustomIndicator(panelId, defId)}
          onDetachCustom={(instanceId) => detachCustomIndicator(panelId, instanceId)}
          onToggleCustom={(instanceId) => toggleCustomIndicatorEnabled(panelId, instanceId)}
          onReorderCustom={(from, to) => reorderCustomIndicators(panelId, from, to)}
          onClose={() => setIsManagerOpen(false)}
        />
      )}

      {/* Unified script editor (Custom JS + Pine Script) */}
      {isCustomIndicatorOpen && (
        <CustomIndicatorPanel
          onClose={() => setIsCustomIndicatorOpen(false)}
          onAttachToChart={(defId) => attachCustomIndicator(panelId, defId)}
          errors={customIndicatorErrors}
        />
      )}
    </div>
  )
}

function NavBtn({
  onClick,
  title,
  children,
}: {
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex h-6 w-6 items-center justify-center text-tv-text-muted transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
    >
      {children}
    </button>
  )
}
